<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-12">
        <div class="card mb-4">
          <div class="card-header pb-0">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h6>Matriz de Permisos de Usuarios</h6>
                <p class="text-sm mb-0">Gestiona los permisos de todos los usuarios de forma visual</p>
              </div>
              <div class="d-flex gap-2">
                <button 
                  class="btn btn-success btn-sm" 
                  @click="saveAllChanges"
                  :disabled="saving || !hasChanges"
                >
                  <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status"></span>
                  <i v-else class="ri-save-line me-1"></i>
                  {{ saving ? 'Guardando...' : 'Guardar Cambios' }}
                </button>
                <button class="btn btn-outline-secondary btn-sm" @click="$emit('close')">
                  <i class="ri-close-line me-1"></i>
                  Cerrar
                </button>
              </div>
            </div>
            
            <!-- Barra de búsqueda -->
            <div class="row align-items-center">
              <div class="col-md-6">
                <div class="position-relative">
                  <i class="ri-search-line search-icon"></i>
                  <input
                    type="text"
                    class="form-control search-input"
                    placeholder="Buscar por nombre o email..."
                    v-model="searchQuery"
                    @input="onSearchInput"
                  />
                  <button 
                    v-if="searchQuery" 
                    class="btn btn-link clear-button" 
                    type="button" 
                    @click="clearSearch"
                    title="Limpiar búsqueda"
                  >
                    <i class="ri-close-line"></i>
                  </button>
                </div>
              </div>
              <div class="col-md-6 d-flex align-items-center justify-content-end">
                <small class="text-muted">
                  {{ filteredUsers.length }} de {{ users.length }} usuarios
                </small>
              </div>
            </div>
          </div>
          
          <div class="card-body px-0 pt-0 pb-2">
            <div v-if="error" class="alert alert-danger mx-4 my-2">
              {{ error }}
            </div>
            
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-success" role="status">
                <span class="visually-hidden">Cargando...</span>
              </div>
              <p class="mt-2">Cargando matriz de permisos...</p>
            </div>
            
            <div v-else class="matrix-container">
              <!-- Controles superiores -->
              <div class="matrix-controls px-4 py-3 border-bottom">
                <div class="row align-items-center">
                  <div class="col-md-6">
                    <div class="form-check form-switch">
                      <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="selectAllMatrix"
                        :checked="allPermissionsSelected"
                        @change="toggleAllMatrix"
                      >
                      <label class="form-check-label fw-bold" for="selectAllMatrix">
                        Seleccionar todos los permisos
                      </label>
                    </div>
                  </div>
                  <div class="col-md-6 text-end">
                    <small class="text-muted">
                      {{ changeCount }} cambios pendientes
                    </small>
                  </div>
                </div>
              </div>
              
              <!-- Matriz de permisos -->
              <div class="matrix-scroll">
                <table class="matrix-table">
                  <thead>
                    <tr>
                      <th class="user-column sticky-column">
                        <div class="header-content">
                          <i class="ri-user-line me-2"></i>
                          Usuario
                        </div>
                      </th>
                      <th 
                        v-for="module in availableModules" 
                        :key="module.id"
                        class="module-column"
                        :title="module.description"
                      >
                        <div class="header-content">
                          <i :class="module.icon" class="module-icon"></i>
                          <div class="module-info">
                            <div class="module-name">{{ module.name }}</div>
                            <div class="module-code">{{ module.code }}</div>
                          </div>
                          <div class="module-controls">
                            <button 
                              class="btn-column-toggle"
                              @click="toggleColumnPermissions(module.id)"
                              :title="`Toggle all ${module.name} permissions`"
                            >
                              <i class="ri-checkbox-line"></i>
                            </button>
                          </div>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="user in filteredUsers" :key="user.id" class="user-row">
                      <td class="user-cell sticky-column">
                        <div class="user-info">
                          <div class="user-avatar">
                            <i class="ri-user-fill"></i>
                          </div>
                          <div class="user-details">
                            <div class="user-name">{{ user.name }}</div>
                            <div class="user-email">{{ user.email }}</div>
                            <div class="user-status">
                              <span :class="`status-badge ${user.is_active ? 'active' : 'inactive'}`">
                                {{ user.is_active ? 'Activo' : 'Inactivo' }}
                              </span>
                            </div>
                          </div>
                          <div class="user-controls">
                            <button 
                              class="btn-row-toggle"
                              @click="toggleRowPermissions(user.id)"
                              :title="`Toggle all permissions for ${user.name}`"
                            >
                              <i class="ri-checkbox-line"></i>
                            </button>
                          </div>
                        </div>
                      </td>
                      <td 
                        v-for="module in availableModules" 
                        :key="`${user.id}-${module.id}`"
                        class="permission-cell"
                        :class="{ 'has-permission': userPermissions[user.id]?.[module.id] }"
                      >
                        <div class="permission-toggle">
                          <input 
                            type="checkbox"
                            :id="`perm-${user.id}-${module.id}`"
                            v-model="userPermissions[user.id][module.id]"
                            @change="onPermissionChange"
                            class="permission-checkbox"
                          >
                          <label 
                            :for="`perm-${user.id}-${module.id}`"
                            class="permission-label"
                          >
                            <i class="ri-check-line check-icon"></i>
                          </label>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <!-- Información de cambios -->
              <div v-if="hasChanges" class="changes-summary px-4 py-3 border-top bg-light">
                <div class="row align-items-center">
                  <div class="col-md-8">
                    <h6 class="mb-1">Cambios pendientes:</h6>
                    <div class="changes-list">
                      <span 
                        v-for="change in pendingChanges" 
                        :key="`${change.userId}-${change.moduleId}`"
                        class="change-item"
                      >
                        <strong>{{ getUserName(change.userId) }}</strong>
                        {{ change.hasAccess ? 'obtuvo' : 'perdió' }} acceso a
                        <strong>{{ getModuleName(change.moduleId) }}</strong>
                      </span>
                    </div>
                  </div>
                  <div class="col-md-4 text-end">
                    <button 
                      class="btn btn-outline-secondary btn-sm me-2" 
                      @click="resetChanges"
                    >
                      <i class="ri-refresh-line me-1"></i>
                      Descartar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "UserPermissionsMatrix",
  emits: ['close'],
  data() {
    return {
      users: [],
      loading: true,
      saving: false,
      error: null,
      // Para búsqueda
      searchQuery: "",
      availableModules: [
        { 
          id: 'dashboard', 
          name: 'Panel Principal', 
          code: 'dashboard',
          icon: 'ri-dashboard-line',
          description: 'Acceso al panel principal de la aplicación' 
        },
        { 
          id: 'bulk_update', 
          name: 'Actualización Masiva', 
          code: 'bulk_update',
          icon: 'ri-upload-2-line',
          description: 'Realizar actualizaciones masivas de datos' 
        },
        { 
          id: 'bulk_loads', 
          name: 'Cargas Másivas', 
          code: 'bulk_loads',
          icon: 'ri-upload-cloud-line',
          description: 'Gestión de cargas masivas de datos' 
        },
        { 
          id: 'celsig5', 
          name: 'CelSig5 y SCeGR', 
          code: 'celsig5',
          icon: 'ri-settings-line',
          description: 'Acceso a los módulos CelSig5 y SCeGR' 
        },
        { 
          id: 'connectivity', 
          name: 'CON - Conectividad', 
          code: 'connectivity',
          icon: 'ri-wifi-line',
          description: 'Gestión de conectividad' 
        },
        { 
          id: 'locations', 
          name: 'Localizaciones Celsig', 
          code: 'locations',
          icon: 'ri-map-pin-line',
          description: 'Gestión de localizaciones' 
        },
        { 
          id: 'delete_records', 
          name: 'Borrado Registros', 
          code: 'delete_records',
          icon: 'ri-delete-bin-line',
          description: 'Gestión de borrado de registros' 
        },
        { 
          id: 'cargar_export', 
          name: 'Cargar Export', 
          code: 'cargar_export',
          icon: 'ri-upload-line',
          description: 'Carga y gestión de archivos de exportación' 
        },
        { 
          id: 'activity', 
          name: 'Actividad', 
          code: 'activity',
          icon: 'ri-line-chart-line',
          description: 'Monitoreo y registro de actividad del sistema' 
        },
        { 
          id: 'users', 
          name: 'Usuarios', 
          code: 'users',
          icon: 'ri-user-settings-line',
          description: 'Gestión de usuarios y permisos' 
        },
        { 
          id: 'admin', 
          name: 'Admin', 
          code: 'admin',
          icon: 'ri-admin-line',
          description: 'Panel de administración general' 
        }
      ],
      userPermissions: {},
      originalPermissions: {}
    };
  },
  computed: {
    filteredUsers() {
      if (!this.searchQuery || this.searchQuery.trim() === '') {
        return this.users;
      }
      
      const query = this.searchQuery.toLowerCase().trim();
      return this.users.filter(user => {
        const name = user.name ? user.name.toLowerCase() : '';
        const email = user.email ? user.email.toLowerCase() : '';
        return name.includes(query) || email.includes(query);
      });
    },
    allPermissionsSelected() {
      return this.filteredUsers.every(user => 
        this.availableModules.every(module => 
          this.userPermissions[user.id]?.[module.id]
        )
      );
    },
    hasChanges() {
      return this.pendingChanges.length > 0;
    },
    changeCount() {
      return this.pendingChanges.length;
    },
    pendingChanges() {
      const changes = [];
      this.users.forEach(user => {
        this.availableModules.forEach(module => {
          const current = this.userPermissions[user.id]?.[module.id] || false;
          const original = this.originalPermissions[user.id]?.[module.id] || false;
          if (current !== original) {
            changes.push({
              userId: user.id,
              moduleId: module.id,
              hasAccess: current
            });
          }
        });
      });
      return changes;
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true;
        this.error = null;
        
        const apiUrl = process.env.VUE_APP_API_BASE_URL || 'http://teleconnect.test/api/v2';
        const tokenJWT = localStorage.getItem('auth_token');
        
        // Obtener usuarios
        const usersResponse = await axios.get(`${apiUrl}/users`);
        this.users = usersResponse.data.data.map(user => ({
          ...user,
          is_active: user.is_active === 1 || user.is_active === true
        }));
        
        // Inicializar estructura de permisos
        this.userPermissions = {};
        this.originalPermissions = {};
        
        // Obtener permisos para cada usuario
        for (const user of this.users) {
          this.userPermissions[user.id] = {};
          this.originalPermissions[user.id] = {};
          
          try {
            const permissionsResponse = await axios.get(`${apiUrl}/users/${user.id}/permissions`, {
              headers: {
                'Authorization': `Bearer ${tokenJWT}`,
                'Accept': 'application/json'
              }
            });
            
            const userPerms = permissionsResponse.data.data || {};
            
            // Inicializar todos los módulos como false
            this.availableModules.forEach(module => {
              const hasPermission = userPerms[module.id] || false;
              this.userPermissions[user.id][module.id] = hasPermission;
              this.originalPermissions[user.id][module.id] = hasPermission;
            });
            
          } catch (permError) {
            console.warn(`Error loading permissions for user ${user.id}:`, permError);
            // Inicializar como false si hay error
            this.availableModules.forEach(module => {
              this.userPermissions[user.id][module.id] = false;
              this.originalPermissions[user.id][module.id] = false;
            });
          }
        }
        
      } catch (error) {
        console.error("Error fetching data:", error);
        this.error = error.response ? 
          `Error: ${error.response.status} - ${error.response.data.message || 'No se pudieron cargar los datos'}` : 
          'Error de conexión al servidor';
      } finally {
        this.loading = false;
      }
    },
    
    onPermissionChange() {
      // Los cambios se detectan automáticamente a través de computed properties
    },
    
    toggleAllMatrix(event) {
      const value = event.target.checked;
      this.filteredUsers.forEach(user => {
        this.availableModules.forEach(module => {
          this.userPermissions[user.id][module.id] = value;
        });
      });
    },
    
    toggleColumnPermissions(moduleId) {
      const allUsersHavePermission = this.filteredUsers.every(user => 
        this.userPermissions[user.id][moduleId]
      );
      const newValue = !allUsersHavePermission;
      
      this.filteredUsers.forEach(user => {
        this.userPermissions[user.id][moduleId] = newValue;
      });
    },
    
    toggleRowPermissions(userId) {
      const userHasAllPermissions = this.availableModules.every(module => 
        this.userPermissions[userId][module.id]
      );
      const newValue = !userHasAllPermissions;
      
      this.availableModules.forEach(module => {
        this.userPermissions[userId][module.id] = newValue;
      });
    },
    
    async saveAllChanges() {
      try {
        this.saving = true;
        
        const apiUrl = process.env.VUE_APP_API_BASE_URL || 'http://teleconnect.test/api/v2';
        const tokenJWT = localStorage.getItem('auth_token');
        
        // Guardar cambios para cada usuario que tenga modificaciones
        const savePromises = [];
        
        this.users.forEach(user => {
          const hasUserChanges = this.availableModules.some(module => {
            const current = this.userPermissions[user.id][module.id];
            const original = this.originalPermissions[user.id][module.id];
            return current !== original;
          });
          
          if (hasUserChanges) {
            const permissionsToUpdate = {};
            this.availableModules.forEach(module => {
              permissionsToUpdate[module.id] = this.userPermissions[user.id][module.id];
            });
            
            const promise = axios.put(`${apiUrl}/users/${user.id}/permissions`, {
              permissions: permissionsToUpdate
            }, {
              headers: {
                'Authorization': `Bearer ${tokenJWT}`,
                'Accept': 'application/json'
              }
            });
            
            savePromises.push(promise);
          }
        });
        
        await Promise.all(savePromises);
        
        // Actualizar permisos originales
        this.users.forEach(user => {
          this.availableModules.forEach(module => {
            this.originalPermissions[user.id][module.id] = this.userPermissions[user.id][module.id];
          });
        });
        
        this.$notify({
          title: 'Éxito',
          message: `Se guardaron correctamente ${this.changeCount} cambios de permisos`,
          type: 'success',
          duration: 3000
        });
        
      } catch (error) {
        console.error("Error saving permissions:", error);
        this.$notify({
          title: 'Error',
          message: 'Error al guardar los cambios de permisos',
          type: 'error',
          duration: 3000
        });
      } finally {
        this.saving = false;
      }
    },
    
    resetChanges() {
      this.users.forEach(user => {
        this.availableModules.forEach(module => {
          this.userPermissions[user.id][module.id] = this.originalPermissions[user.id][module.id];
        });
      });
    },
    
    getUserName(userId) {
      return this.users.find(u => u.id === userId)?.name || 'Usuario';
    },
    
    getModuleName(moduleId) {
      return this.availableModules.find(m => m.id === moduleId)?.name || 'Módulo';
    },
    
    // Métodos para la búsqueda
    onSearchInput() {
      // Este método se puede usar para debouncing si es necesario en el futuro
      // Por ahora la computed property se actualiza automáticamente
    },
    
    clearSearch() {
      this.searchQuery = "";
    }
  }
};
</script>

<style scoped>
.matrix-container {
  background: var(--dm-bg-primary);
  color: var(--dm-text-primary);
}

.matrix-controls {
  background: var(--dm-bg-secondary);
  border-bottom: 1px solid var(--dm-border-color);
}

.matrix-scroll {
  overflow-x: auto;
  max-height: 70vh;
  border: 1px solid var(--dm-border-color);
}

.matrix-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.875rem;
}

.matrix-table th,
.matrix-table td {
  border-right: 1px solid var(--dm-border-color);
  border-bottom: 1px solid var(--dm-border-color);
  background: var(--dm-bg-primary);
}

.sticky-column {
  position: sticky;
  left: 0;
  z-index: 10;
  background: var(--dm-bg-primary) !important;
  box-shadow: 2px 0 4px var(--dm-shadow-light);
}

.user-column {
  width: 300px;
  min-width: 300px;
  background: var(--dm-bg-secondary) !important;
}

.module-column {
  width: 120px;
  min-width: 120px;
  background: var(--dm-bg-secondary) !important;
  text-align: center;
  vertical-align: top;
  padding: 8px 4px;
}

.header-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  height: 100%;
}

.module-icon {
  font-size: 1.2rem;
  color: var(--dm-text-secondary);
}

.module-info {
  text-align: center;
}

.module-name {
  font-weight: 600;
  font-size: 0.75rem;
  line-height: 1.2;
  color: var(--dm-text-primary);
}

.module-code {
  font-size: 0.65rem;
  color: var(--dm-text-secondary);
  font-family: monospace;
}

.module-controls, .user-controls {
  margin-top: 4px;
}

.btn-column-toggle, .btn-row-toggle {
  background: none;
  border: none;
  color: var(--dm-text-secondary);
  padding: 2px 4px;
  border-radius: 3px;
  transition: all 0.2s;
  font-size: 0.75rem;
}

.btn-column-toggle:hover, .btn-row-toggle:hover {
  background: var(--dm-bg-secondary);
  color: var(--dm-text-primary);
}

.user-cell {
  padding: 12px;
  background: var(--dm-bg-secondary) !important;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--dm-accent-success), #449d44);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--dm-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-email {
  font-size: 0.75rem;
  color: var(--dm-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-status {
  margin-top: 2px;
}

.status-badge {
  font-size: 0.65rem;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 500;
}

.status-badge.active {
  background: var(--dm-accent-success);
  color: white;
}

.status-badge.inactive {
  background: var(--dm-accent-danger);
  color: white;
}

.permission-cell {
  text-align: center;
  vertical-align: middle;
  padding: 8px;
  transition: background-color 0.2s;
}

.permission-cell:hover {
  background: var(--dm-bg-secondary);
}

.permission-cell.has-permission {
  background: rgba(92, 184, 92, 0.1);
}

.permission-toggle {
  position: relative;
  display: inline-block;
}

.permission-checkbox {
  display: none;
}

.permission-label {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 2px solid var(--dm-border-color);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: var(--dm-bg-primary);
}

.permission-label:hover {
  border-color: var(--dm-accent-success);
  transform: scale(1.05);
}

.permission-checkbox:checked + .permission-label {
  background: var(--dm-accent-success);
  border-color: var(--dm-accent-success);
  color: white;
}

.check-icon {
  font-size: 1.1rem;
  opacity: 0;
  transition: opacity 0.2s;
}

.permission-checkbox:checked + .permission-label .check-icon {
  opacity: 1;
}

.changes-summary {
  border-top: 2px solid var(--dm-accent-success);
  background: var(--dm-bg-secondary);
}

.changes-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 0.8rem;
}

.change-item {
  background: var(--dm-bg-primary);
  padding: 4px 8px;
  border-radius: 12px;
  border: 1px solid var(--dm-border-color);
  color: var(--dm-text-primary);
}

/* Estilos para la barra de búsqueda */
.search-input {
  padding-left: 2.5rem;
  padding-right: 2.5rem;
  height: 38px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  transition: all 0.3s ease;
}

.search-input:focus {
  border-color: #5cb85c;
  box-shadow: 0 0 0 0.2rem rgba(92, 184, 92, 0.25);
  outline: none;
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  font-size: 1rem;
  z-index: 2;
  pointer-events: none;
}

.clear-button {
  position: absolute;
  right: 0.25rem;
  top: 50%;
  transform: translateY(-50%);
  width: 30px;
  height: 30px;
  border-radius: 50%;
  padding: 0;
  color: #6c757d;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.clear-button:hover {
  color: #dc3545;
  background-color: #f8f9fa;
}

.clear-button:focus {
  box-shadow: none;
}

/* Estilos para barra de búsqueda en modo oscuro */
body.dark-mode-active .search-input {
  background-color: #2d2d2d !important;
  border-color: #495057 !important;
  color: #ffffff !important;
}

body.dark-mode-active .search-input:focus {
  border-color: #00ffb3 !important;
  box-shadow: 0 0 0 0.2rem rgba(0, 255, 179, 0.25) !important;
}

body.dark-mode-active .search-input::placeholder {
  color: #adb5bd !important;
}

body.dark-mode-active .search-icon {
  color: #adb5bd !important;
}

body.dark-mode-active .clear-button {
  color: #adb5bd !important;
}

body.dark-mode-active .clear-button:hover {
  color: #ff6b6b !important;
  background-color: #495057 !important;
}

/* Estilos adicionales para dark mode */
body.dark-mode-active .matrix-container {
  background: var(--dm-bg-primary);
  color: var(--dm-text-primary);
}

body.dark-mode-active .matrix-controls {
  background: var(--dm-bg-secondary);
  border-bottom-color: var(--dm-border-color);
}

body.dark-mode-active .matrix-scroll {
  border-color: var(--dm-border-color);
}

body.dark-mode-active .matrix-table th,
body.dark-mode-active .matrix-table td {
  border-color: var(--dm-border-color);
  background: var(--dm-bg-primary);
}

body.dark-mode-active .sticky-column {
  background: var(--dm-bg-primary) !important;
  box-shadow: 2px 0 4px var(--dm-shadow-dark);
}

body.dark-mode-active .user-column,
body.dark-mode-active .module-column,
body.dark-mode-active .user-cell {
  background: var(--dm-bg-secondary) !important;
}

body.dark-mode-active .permission-cell.has-permission {
  background: rgba(52, 211, 153, 0.2);
}

body.dark-mode-active .changes-summary {
  background: var(--dm-bg-secondary);
}

body.dark-mode-active .change-item {
  background: var(--dm-bg-primary);
  border-color: var(--dm-border-color);
  color: var(--dm-text-primary);
}

@media (max-width: 768px) {
  .user-column {
    width: 250px;
    min-width: 250px;
  }
  
  .module-column {
    width: 100px;
    min-width: 100px;
  }
  
  .user-info {
    gap: 8px;
  }
  
  .user-avatar {
    width: 30px;
    height: 30px;
    font-size: 1rem;
  }
}
</style>